<template>
  <div
    class="create-workflow-overlay"
    role="dialog"
    aria-modal="true"
    aria-label="Create activity"
    @click.self="$emit('close')"
  >
    <div class="create-workflow-modal">
      <div class="create-workflow-header">
        <div>
          <h4>Create Activity</h4>
          <p>{{ workflowName }}</p>
        </div>
        <button
          type="button"
          class="workflow-modal-close-btn"
          aria-label="Close activity creation"
          @click="$emit('close')"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <form class="create-workflow-form" @submit.prevent="$emit('form-submit')">
        <div class="form-group">
          <label for="hierarchy-action-name">Name</label>
          <input
            id="hierarchy-action-name"
            :value="form.name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.includes('name') }"
            required
            @input="onFieldInput('name', $event.target.value)"
          />
          <div v-if="errors.includes('name')" class="error-feedback">Name is required.</div>
        </div>

        <div class="form-group">
          <label for="hierarchy-action-description">Description</label>
          <textarea
            id="hierarchy-action-description"
            :value="form.description"
            class="form-control form-control-textarea"
            :class="{ 'is-invalid': errors.includes('description') }"
            rows="3"
            @input="onFieldInput('description', $event.target.value)"
          ></textarea>
          <div v-if="errors.includes('description')" class="error-feedback">Description is required.</div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="hierarchy-action-status">Status</label>
            <select
              id="hierarchy-action-status"
              :value="form.status"
              class="form-control"
              :class="{ 'is-invalid': errors.includes('status') }"
              @change="onFieldChange('status', $event.target.value)"
            >
              <option value="">Select status</option>
              <option value="contract">Contract</option>
              <option value="to be started">To be started</option>
              <option value="ongoing">Ongoing</option>
              <option value="finished">Finished</option>
            </select>
            <div v-if="errors.includes('status')" class="error-feedback">Status is required.</div>
          </div>

          <div class="form-group">
            <label for="hierarchy-action-location">Location</label>
            <select
              id="hierarchy-action-location"
              :value="form.location_id"
              class="form-control"
              :class="{ 'is-invalid': errors.includes('location_id') }"
              @change="onFieldChange('location_id', $event.target.value)"
            >
              <option value="">Select a location</option>
              <option v-for="location in locations" :key="location.id" :value="location.id">
                {{ location.name }}
              </option>
            </select>
            <div v-if="errors.includes('location_id')" class="error-feedback">Location is required.</div>
          </div>
        </div>

        <div class="form-group">
          <label for="hierarchy-action-workflow">Workflow</label>
          <input
            id="hierarchy-action-workflow"
            class="form-control"
            type="text"
            :value="workflowName"
            disabled
          />
        </div>

        <div class="create-workflow-actions">
          <button type="button" class="btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="submit" class="btn-primary" :disabled="isSubmitting">
            <span v-if="isSubmitting">Creating...</span>
            <span v-else>Create activity</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HierarchyCreateActionModal',
  props: {
    workflowName: {
      type: String,
      default: '',
    },
    form: {
      type: Object,
      required: true,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    locations: {
      type: Array,
      default: () => [],
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    onFieldInput(field, value) {
      this.$emit('update-field', field, value);
      this.$emit('validate-field', field, value);
    },
    onFieldChange(field, value) {
      this.$emit('update-field', field, value);
      this.$emit('validate-field', field, value);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/variables.scss';

.create-workflow-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(0, 0, 0, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.create-workflow-modal {
  width: min(560px, 100%);
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
  padding: 24px;
}

.create-workflow-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;

  h4 {
    margin: 0;
    color: $primary-color;
  }

  p {
    margin: 4px 0 0;
    color: #5a5a5a;
  }
}

.workflow-modal-close-btn {
  border: 1px solid rgba($primary-color, 0.18);
  background: rgba(255, 255, 255, 0.92);
  color: $primary-color;
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.create-workflow-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-weight: 600;
    color: $primary-color;
  }
}

.form-control {
  width: 100%;
  min-height: 42px;
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  color: #222;
}

.form-control-textarea {
  min-height: 96px;
  resize: vertical;
}

.form-control:disabled {
  background: #f3f5f8;
  color: #667085;
}

.form-control.is-invalid {
  border-color: #b00020;
}

.error-feedback {
  color: #b00020;
  font-size: 12px;
}

.create-workflow-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.btn-secondary {
  border: 1px solid rgba($primary-color, 0.2);
  background: #fff;
  color: $primary-color;
  border-radius: 10px;
  padding: 10px 18px;
  cursor: pointer;
}

.btn-primary {
  border: none;
  background: $primary-color;
  color: #fff;
  border-radius: 10px;
  padding: 10px 18px;
  cursor: pointer;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .create-workflow-modal {
    padding: 18px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
